import React, { useState } from "react";
import { Icon } from "../icons";
import { addDays, fmtDate, fmtDateTime, todayStr } from "../lib/format";
import { SectionLabel } from "./fields";

const tomorrow = () => addDays(todayStr(), 1);

const reminderWhen = (issue) =>
  `Reminder ${fmtDate(issue.remDate)}${issue.remTime ? " " + issue.remTime : ""}${
    issue.remNote ? " · " + issue.remNote : ""
  }`;

/** "Log a new issue" form: text, optional reminder (No/Yes). */
const NewIssueForm = ({ onSave }) => {
  const [text, setText] = useState("");
  const [remind, setRemind] = useState(false);
  const [remDate, setRemDate] = useState(tomorrow());
  const [remTime, setRemTime] = useState("");
  const [remNote, setRemNote] = useState("");
  const [error, setError] = useState("");

  const save = () => {
    if (!text.trim()) return setError("Describe the issue before saving.");
    if (remind && !remDate) return setError("Pick a date for the reminder.");
    onSave({
      text: text.trim(),
      remDate: remind ? remDate : "",
      remTime: remind ? remTime : "",
      remNote: remind ? remNote.trim() : "",
    });
    setText("");
    setRemind(false);
    setRemDate(tomorrow());
    setRemTime("");
    setRemNote("");
    setError("");
  };

  return (
    <div className="liab-block">
      <div>
        <div>Log a new issue</div>
        <div className="liab-sub">What happened and where does it stand?</div>
      </div>
      <textarea
        rows={3}
        aria-label="Log a new issue"
        placeholder="e.g. Damaged dresser — claim form sent, waiting on photos from the client..."
        value={text}
        onChange={(e) => setText(e.target.value)}
      />
      <div className="liab-top" style={{ marginTop: 10 }}>
        <div>
          <div>Reminder</div>
          <div className="liab-sub">Add a reminder to this job</div>
        </div>
        <div className="seg" role="group" aria-label="Reminder">
          <button
            type="button"
            className={remind ? "" : "on-no"}
            aria-pressed={!remind}
            onClick={() => setRemind(false)}
          >
            No
          </button>
          <button
            type="button"
            className={remind ? "on-yes" : ""}
            aria-pressed={remind}
            onClick={() => setRemind(true)}
          >
            Yes
          </button>
        </div>
      </div>
      {remind ? (
        <div style={{ marginTop: 8 }}>
          <div className="field-row" style={{ margin: "0 0 6px 0" }}>
            <div>
              <div className="liab-sub" style={{ margin: "0 0 3px 0" }}>
                Remind me on
              </div>
              <input
                type="date"
                aria-label="Remind me on"
                value={remDate}
                onChange={(e) => setRemDate(e.target.value)}
              />
            </div>
            <div>
              <div className="liab-sub" style={{ margin: "0 0 3px 0" }}>
                At (optional)
              </div>
              <input
                type="time"
                aria-label="Reminder time"
                value={remTime}
                onChange={(e) => setRemTime(e.target.value)}
              />
            </div>
          </div>
          <input
            type="text"
            aria-label="Reminder note"
            placeholder="What is the reminder? (e.g. Call client about the claim)"
            value={remNote}
            onChange={(e) => setRemNote(e.target.value)}
          />
        </div>
      ) : null}
      {error ? (
        <div className="pm-error" role="alert">
          {error}
        </div>
      ) : null}
      <div className="toolbar-row" style={{ marginTop: 10 }}>
        <button type="button" className="btn primary small" onClick={save}>
          <Icon name="check" />
          Save issue
        </button>
      </div>
    </div>
  );
};

const IssueReminder = ({ issue, onSave }) => {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState(tomorrow());
  const [time, setTime] = useState("");
  const [note, setNote] = useState("");

  if (issue.remDate) {
    return (
      <div style={{ marginTop: 6 }}>
        <span className="badge open">
          <Icon name="bell" />
          {reminderWhen(issue)}
        </span>
      </div>
    );
  }
  return (
    <div style={{ marginTop: 6 }}>
      <button type="button" className="btn small" onClick={() => setOpen((v) => !v)}>
        <Icon name="bell" />
        Add reminder
      </button>
      {open ? (
        <div style={{ marginTop: 6 }}>
          <div className="field-row" style={{ margin: "0 0 6px 0" }}>
            <input
              type="date"
              aria-label="Issue reminder date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
            <input
              type="time"
              aria-label="Issue reminder time"
              value={time}
              onChange={(e) => setTime(e.target.value)}
            />
          </div>
          <div className="pm-inline-row" style={{ marginTop: 0 }}>
            <input
              type="text"
              aria-label="Issue reminder note"
              placeholder="What is the reminder?"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
            <button
              type="button"
              className="btn primary small"
              disabled={!date}
              onClick={() => {
                if (!date) return;
                onSave({ date, time, note });
                setOpen(false);
              }}
            >
              Save
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

const IssueComments = ({ issue, onAdd }) => {
  const [text, setText] = useState("");
  const submit = () => {
    if (!text.trim()) return;
    onAdd(text.trim());
    setText("");
  };
  return (
    <>
      {(issue.comments || []).map((c, i) => (
        <div className="note-item" style={{ marginTop: 6 }} key={i}>
          {c.text}
          <div className="meta">
            {c.author || "MC"} · {fmtDateTime(c.date)}
          </div>
        </div>
      ))}
      <div className="pm-inline-row">
        <input
          type="text"
          aria-label="Add a comment"
          placeholder="Add a comment..."
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") submit();
          }}
        />
        <button type="button" className="btn small" onClick={submit}>
          Add
        </button>
      </div>
    </>
  );
};

/** Post-Move Ongoing Issues: log, remind, comment on and resolve issues. Newest first. */
export const PmIssues = ({ job, actions }) => {
  const issues = Array.isArray(job.pmIssues) ? job.pmIssues : [];
  return (
    <>
      <SectionLabel icon="alert">Post-Move Ongoing Issues</SectionLabel>
      <div className="liab-card">
        <NewIssueForm onSave={(issue) => actions.addPmIssue(job.id, issue)} />
        {issues.map((i) => (
          <div className="liab-block" key={i.id} data-testid="pm-issue">
            <div className="liab-top">
              <div>
                <div>{i.text}</div>
                <div className="liab-sub">
                  {i.author || "MC"} · {fmtDateTime(i.date)}
                </div>
              </div>
              <button
                type="button"
                className={`btn small${i.status === "resolved" ? " success" : ""}`}
                onClick={() => actions.togglePmIssue(job.id, i.id)}
              >
                <Icon name="check" />
                {i.status === "resolved" ? "Resolved" : "Mark resolved"}
              </button>
            </div>
            <IssueReminder
              issue={i}
              onSave={(reminder) => actions.setPmIssueReminder(job.id, i.id, reminder)}
            />
            <IssueComments issue={i} onAdd={(t) => actions.addPmIssueComment(job.id, i.id, t)} />
          </div>
        ))}
      </div>
    </>
  );
};
